import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {PullRequestStateIcons} from '@github-ui/issue-pr-state/Icons'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useAnalytics} from '@github-ui/use-analytics'
import {CheckCircleFillIcon, CommentIcon, DashIcon, MilestoneIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {Link, RelativeTime} from '@primer/react'
import React from 'react'
import styles from './PullsInboxSection.module.css'
import {unreadRowClassName, UnreadRowLabel} from '../shared/UnreadRowIndicator'
import inlineCodeStyles from '../shared/TitleInlineCode.module.css'
import type {EnrichedPull} from './types/list-types'
import {getCompositeCategoryDisplay, inboxReviewTooltipLabel, statusCheckColumnDisplay} from './helpers/inbox-helpers'
import {NonInteractiveTooltip} from './helpers/NonInteractiveTooltip'
import {PR_STATE_FALLBACK_TOOLTIP_LABEL, PR_STATE_TOOLTIP_LABELS} from './consts/pr-state-consts'
import {PullsChecksStatusBadge} from './PullsChecksStatusBadge'
import {PullsInboxChecksSkeleton} from './PullsInboxSectionSkeletonItem'
import {StackBadge} from '@github-ui/stack-badge'
import {ProfileReference} from '@github-ui/profile-reference'
import {deriveAttributedUsername, isAgentAuthor} from './helpers/derive-attributed-username'

interface PullsInboxListItemProps {
  row: EnrichedPull
  isAgentAttributionEnabled?: boolean
}

// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
export const PullsInboxListItem = React.memo(function PullsInboxListItem({
  row,
  isAgentAttributionEnabled,
}: PullsInboxListItemProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const {isBrowser} = useRuntimeEnvironment()

  const PRStateIcon = row.displayState
    ? PullRequestStateIcons[row.displayState as keyof typeof PullRequestStateIcons]
    : undefined
  const prStateTooltipLabel = row.displayState
    ? (PR_STATE_TOOLTIP_LABELS[row.displayState as keyof typeof PullRequestStateIcons] ??
      PR_STATE_FALLBACK_TOOLTIP_LABEL)
    : PR_STATE_FALLBACK_TOOLTIP_LABEL
  const [owner, repo] = row.repoNameWithOwner.split('/')
  const hovercardProps = getHovercardAttributesForType('pull_request', {
    owner,
    repo,
    pullRequestNumber: row.number,
  })

  const attributedUsername = isAgentAttributionEnabled ? deriveAttributedUsername(row) : undefined
  const isPullAgentAuthored = isAgentAttributionEnabled ? isAgentAuthor(row) : false

  const display = row.category ? getCompositeCategoryDisplay(row.category, row.statusCheckRollup) : null
  const reviewTooltipLabel = row.category
    ? inboxReviewTooltipLabel(row.category, {
        approvedReviewsCount: row.approvedReviewsCount,
        changesRequestedReviewsCount: row.changesRequestedReviewsCount,
      })
    : undefined
  const IconComponent = display?.icon
  const commitChecksDialogEnabled = isBrowser

  const categoryBadge = display ? (
    <span className={styles.categoryBadge} data-variant={display.variant}>
      {IconComponent ? (
        <span style={display.iconColor ? {color: display.iconColor} : undefined}>
          <IconComponent
            className={styles.statusColumnIcon}
            size={12}
            data-testid="status-indicator"
            aria-label={`${display.label} indicator`}
          />
        </span>
      ) : display.variant === 'success' ? (
        <CheckCircleFillIcon
          size={12}
          className={styles.categoryBadgeSuccessIcon}
          data-testid="status-indicator"
          aria-label="Ready to merge indicator"
        />
      ) : (
        <span className={styles.categoryBadgeDot} data-testid="status-indicator" />
      )}
      {display.label}
    </span>
  ) : null

  return (
    <ListItem
      className={unreadRowClassName(row.isReadByCurrentUser)}
      metadataContainerClassName={styles.metadataContainer}
      title={
        /* The shared inline-code class goes on `className`, not `anchorClassName`:
           ListItemTitle spreads `className` onto the title anchor (where the titleHtml
           <code> renders), so this is what styles inline code like the list view. */
        <ListItemTitle
          className={clsx('mr-2 color-fg-default', inlineCodeStyles.inlineCodeTitle)}
          value={row.titleHtml ? <VerifiedHTMLText as="span" html={row.titleHtml} /> : <span>{row.title}</span>}
          href={row.permalink}
          onClick={() => sendAnalyticsEvent('inbox.click', 'LIST_ITEM_TITLE', {displayState: row.displayState})}
          linkProps={hovercardProps}
        />
      }
      metadata={[
        <ListItemMetadata key="status" className={styles.statusColumn}>
          {categoryBadge ? (
            reviewTooltipLabel ? (
              <NonInteractiveTooltip label={reviewTooltipLabel} direction="s">
                {categoryBadge}
              </NonInteractiveTooltip>
            ) : (
              categoryBadge
            )
          ) : (
            <span className={clsx('color-fg-muted', styles.emptyPlaceholder)}>&mdash;</span>
          )}
        </ListItemMetadata>,
        <ListItemMetadata key="checks" className={styles.checksColumn}>
          {row.deferredDataStatus === 'pending' ? (
            <PullsInboxChecksSkeleton />
          ) : row.statusCheckRollup ? (
            <span className={styles.statusChecks}>
              {commitChecksDialogEnabled && row.headSha ? (
                <PullsChecksStatusBadge
                  headSha={row.headSha}
                  repositoryNwo={row.repoNameWithOwner}
                  statusCheckRollup={row.statusCheckRollup}
                  aliveChannel={row.commitHeadShaChannel}
                />
              ) : (
                statusCheckColumnDisplay(row.statusCheckRollup)
              )}
            </span>
          ) : (
            <span className={clsx('color-fg-muted', styles.emptyPlaceholder)}>&mdash;</span>
          )}
        </ListItemMetadata>,
        <ListItemMetadata key="comments" className={styles.commentsColumn}>
          {row.commentCount > 0 ? (
            <span className={styles.metadataCell}>
              <CommentIcon size={16} /> {row.commentCount}
            </span>
          ) : (
            <span className={clsx('color-fg-muted', styles.emptyPlaceholder)}>&mdash;</span>
          )}
        </ListItemMetadata>,
      ]}
    >
      <ListItemLeadingContent>
        <UnreadRowLabel isRead={row.isReadByCurrentUser} />
        <ListItemLeadingVisual>
          {PRStateIcon ? (
            <NonInteractiveTooltip label={prStateTooltipLabel} direction="e" includeAriaAttributes={false}>
              <PRStateIcon aria-label={prStateTooltipLabel} />
            </NonInteractiveTooltip>
          ) : (
            <NonInteractiveTooltip label={prStateTooltipLabel} direction="e" includeAriaAttributes={false}>
              <DashIcon
                size={16}
                className="color-fg-muted"
                data-testid="fallback-pr-state-icon"
                aria-label={prStateTooltipLabel}
              />
            </NonInteractiveTooltip>
          )}
        </ListItemLeadingVisual>
      </ListItemLeadingContent>
      <ListItemMainContent>
        <ListItemDescription className={styles.description}>
          <ListItemDescriptionItem>
            {row.repoNameWithOwner}#{row.number} &bull;{' '}
            {attributedUsername && (
              <>
                <ProfileReference login={attributedUsername} profileName={attributedUsername} isAgent={false} />{' '}
                with{' '}
              </>
            )}
            {row.author.displayName || (
              <ProfileReference
                login={row.author.displayLogin}
                profileName={row.author.displayLogin}
                isAgent={isPullAgentAuthored}
              />
            )}
            {isBrowser && (
              <>
                {' '}
                &bull; Updated <RelativeTime date={new Date(row.updatedAt)} />
              </>
            )}
          </ListItemDescriptionItem>
          {row.milestone ? (
            <ListItemDescriptionItem>
              <Link href={row.milestone.url} muted aria-label={`Milestone: ${row.milestone.title}`}>
                <MilestoneIcon size={16} /> {row.milestone.title}
              </Link>
            </ListItemDescriptionItem>
          ) : null}
          {row.stackPosition != null && row.stackSize != null && (
            <ListItemDescriptionItem>
              <span className={styles.middot} aria-hidden="true">
                &middot;
              </span>
              <StackBadge
                stackPosition={row.stackPosition}
                stackSize={row.stackSize}
                pullRequestNumber={row.number}
                repoNameWithOwner={row.repoNameWithOwner}
              />
            </ListItemDescriptionItem>
          )}
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
})
